import { trim } from 'lodash';

import {
  convertPhoneNumberToInternationalFormat,
  convertToISOFormat,
  validatePhoneNumber,
} from './patient.util';
import { EquipAppSupportPermissions } from '../constants/supports.constants';
import { formatPhoneNumberForTextInputV2 } from '@equip.health/ui';
import { PatientContactState } from '~/lib/types/patient/PatientContactState';

export const generateSupportPayload = (supportData: PatientContactState) => ({
  addressLine1: supportData.address.addressLine1,
  addressLine2: supportData.address.addressLine2,
  affiliationName: supportData.contactAddress.organization,
  cellPhone: convertPhoneNumberToInternationalFormat(
    supportData.phonesField.mobile,
  ),
  chosenName: supportData.personalInfo.chosenName,
  city: supportData.address.city,
  contactExternalId: supportData?.contactExternalId,
  contactPreferences: supportData.contactInfo.contactPreference,
  country: null,
  dateOfBirth:
    supportData.personalInfo.dateOfBirth &&
    convertToISOFormat(supportData.personalInfo.dateOfBirth),
  department: null,
  email: trim(supportData.contactInfo.email),
  ethnicities: supportData.personalInfo.ethnicities,
  faxNumber: convertPhoneNumberToInternationalFormat(
    supportData.phonesField.fax,
  ),
  firstName: trim(supportData.personalInfo.firstName),
  gender: supportData.personalInfo.gender,
  homePhone: convertPhoneNumberToInternationalFormat(
    supportData.phonesField.home,
  ),
  isEmergencyContact: supportData.personalInfo.isEmergencyContact,
  isGuarantor: supportData.personalInfo.isGuarantor,
  isLivesWithPatient: supportData.contactAddress.livesWithPatient,
  isPrimaryContact: supportData.personalInfo.isPrimaryContact,
  isProxy:
    supportData.personalInfo.permissions === EquipAppSupportPermissions.PROXY,
  languages: supportData.contactInfo.languages,
  lastName: trim(supportData.personalInfo.lastName),
  latitude: supportData.address.latitude,
  longitude: supportData.address.longitude,
  middleName: trim(supportData.personalInfo.middleName),
  officeExtension: supportData.phonesField.extension,
  officeNumber: convertPhoneNumberToInternationalFormat(
    supportData.phonesField.office,
  ),
  patientExternalId: supportData.patientId,
  prefix: trim(supportData.personalInfo.prefix),
  pronouns: supportData.personalInfo.pronouns,
  relationToPatient: supportData.contactInfo.relationToPatient,
  secondaryCellPhone: convertPhoneNumberToInternationalFormat(
    supportData.phonesField.other,
  ),
  state: supportData.address.state || '',
  suffix: trim(supportData.personalInfo.suffix),
  timezone: supportData.address.timezone,
  zip: supportData.address.zipCode,
});

export const getAddPatientContactPayload = (
  contactData: PatientContactState,
  providerId: string,
) => {
  const obj: PatientContact = {
    addressLine1: contactData.address.addressLine1,
    addressLine2: contactData.address.addressLine2,
    affiliationName: contactData.contactAddress.organization,
    cellPhone: convertPhoneNumberToInternationalFormat(
      contactData.phonesField.mobile,
    ),
    chosenName: trim(contactData.personalInfo.chosenName),
    city: contactData.address.city,
    contactPreferences: contactData.contactInfo.contactPreference,
    country: null,
    dateOfBirth:
      contactData.personalInfo.dateOfBirth &&
      convertToISOFormat(contactData.personalInfo.dateOfBirth),
    department: null,
    email: trim(contactData.contactInfo.email),
    ethnicities: contactData.personalInfo.ethnicities,
    faxNumber: convertPhoneNumberToInternationalFormat(
      contactData.phonesField.fax,
    ),
    firstName: trim(contactData.personalInfo.firstName),
    gender: contactData.personalInfo.gender,
    homePhone: convertPhoneNumberToInternationalFormat(
      contactData.phonesField.home,
    ),
    isEmergencyContact: contactData.personalInfo.isEmergencyContact,
    isGuarantor: contactData.personalInfo.isGuarantor,
    isLivesWithPatient: contactData.contactAddress.livesWithPatient,
    isOngoingProvider: contactData.personalInfo.isOngoingProvider,
    isPrimaryContact: contactData.personalInfo.isPrimaryContact,
    isReferringProvider: contactData.personalInfo.isReferringProvider,
    languages: contactData.contactInfo.languages,
    lastName: trim(contactData.personalInfo.lastName),
    latitude: contactData.address.latitude,
    longitude: contactData.address.longitude,
    middleName: trim(contactData.personalInfo.middleName),
    officeExtension: String(contactData.phonesField.extension) || null,
    officePhone:
      convertPhoneNumberToInternationalFormat(contactData.phonesField.office) ||
      null,
    patientExternalId: contactData.patientId,
    prefix: trim(contactData.personalInfo.prefix),
    pronouns: contactData.personalInfo.pronouns,
    providerId,
    relationToPatient: contactData.contactInfo.relationToPatient,
    secondaryCellPhone: convertPhoneNumberToInternationalFormat(
      contactData.phonesField.other,
    ),
    state: contactData.address.state,
    suffix: trim(contactData.personalInfo.suffix),
    timezone: contactData.address.timezone,
    zip: contactData.address.zipCode,
  };

  return obj;
};

export const isContactAddressFieldsEmpty = (
  addressData: GooglePlacesField,
): boolean => {
  return Object.entries(addressData).every(
    ([key, val]: [string, string | number]) => {
      const isEmpty = [
        'addressLine1',
        'addressLine2',
        'city',
        'state',
        'zipCode',
        'timezone',
      ].includes(key)
        ? val === null || val === undefined || val.toString().trim() === ''
        : true;

      return isEmpty;
    },
  );
};

export const getContactRequiredFields = (address): string[] => {
  if (!isContactAddressFieldsEmpty(address)) {
    return ['state'];
  } else return [];
};
export const isValidZipCodeFormat = (zipCode?: string): boolean => {
  const zipPattern = /^(?:\d{5}|\d{5}-\d{4})$/;
  return zipCode ? zipPattern.test(zipCode) : true;
};

export const validateContactPhonesField = (
  phonesField: PhonesFieldType,
  mobileRequired: boolean = false,
): boolean => {
  if (
    (mobileRequired &&
      (!phonesField.mobile || !validatePhoneNumber(phonesField.mobile))) ||
    (!mobileRequired &&
      phonesField.mobile &&
      !validatePhoneNumber(phonesField.mobile))
  ) {
    return false;
  }

  const otherFieldsValid = Object.entries(phonesField).every(([key, value]) => {
    if (key === 'extension' || key === 'mobile') {
      return true;
    }
    return (
      value === null || value === '' || validatePhoneNumber(value.toString())
    );
  });

  return otherFieldsValid;
};

export const applyPatientAddress = (address: Patient) => {
  const {
    addressLine1,
    addressLine2,
    affiliationName,
    city,
    country,
    latitude,
    longitude,
    state,
    timezone,
    zip,
  } = address || {};

  const newAddress = {
    addressLine1,
    addressLine2,
    city,
    country,
    latitude,
    longitude,
    name: affiliationName,
    state,
    timezone,
    zipCode: zip,
  };
  return newAddress;
};

export const EMAIL_ERROR_MESSAGES = {
  PATIENT_EMAIL_MATCH: "Email cannot be the same as patient's",
  SUPPORT_EMAIL_MATCH:
    'The support account matching this email address already has access to this patient. Please enter a unique email address.',
  SUPPORT_REQUIRED: 'Required for Equip App',
};

export const SUPPORT_ACCESS_TOOLTIP_TEXT =
  'Providers cannot have support access in the Equip App.';
export const PROVIDER_TOOLTIP_TEXT =
  'Contact with support access cannot be a provider.';

export const formatPhoneNumberWithCode = (phoneNumber: string): string => {
  if (phoneNumber?.startsWith('+1') && phoneNumber.length === 12) {
    return formatPhoneNumberForTextInputV2(phoneNumber);
  } else {
    return phoneNumber;
  }
};
